import { useFetcher } from "react-router"
import { useState, useEffect } from "react"
import { Icon } from "#app/components/ui/icon"
import { Button } from "#app/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "#app/components/ui/dialog"
import { useOptionalUser } from "#app/utils/user"
import { useWishlist } from "#app/utils/wishlist-context"

const WISHLIST_DIALOG_SHOWN_KEY = "fenix_wishlist_dialog_shown"

type WishlistButtonProps = {
	productId: string
	variant?: "button" | "icon"
	className?: string
}

export function WishlistButton({
	productId,
	variant = "icon",
	className,
}: WishlistButtonProps) {
	const user = useOptionalUser()
	const { isInWishlist: isInLocalWishlist, toggleWishlist: toggleLocal } =
		useWishlist()
	const fetcher = useFetcher()
	const [showAccountDialog, setShowAccountDialog] = useState(false)
	const [hasShownDialog, setHasShownDialog] = useState(false)

	useEffect(() => {
		const shown = localStorage.getItem(WISHLIST_DIALOG_SHOWN_KEY)
		setHasShownDialog(!!shown)
	}, [])

	const isOptimisticallyInWishlist = fetcher.formData
		? fetcher.formData.get("intent") === "toggle"
			? !isInLocalWishlist(productId)
			: isInLocalWishlist(productId)
		: isInLocalWishlist(productId)

	const isInWishlist = user
		? isOptimisticallyInWishlist
		: isInLocalWishlist(productId)

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation()
		e.preventDefault()
		if (!user) {
			toggleLocal(productId)
			if (!isInLocalWishlist(productId) && !hasShownDialog) {
				setShowAccountDialog(true)
				localStorage.setItem(WISHLIST_DIALOG_SHOWN_KEY, "true")
			}
			return
		}

		fetcher.submit(
			{ productId, intent: "toggle" },
			{ method: "POST", action: "/resources/wishlist" },
		)
	}

	if (variant === "button") {
		return (
			<>
				<Button
					type="button"
					variant={isInWishlist ? "secondary" : "outline"}
					onClick={handleToggle}
					className={className}
				>
					<Icon name={isInWishlist ? "heart-filled" : "heart"} />
					{isInWishlist
						? "En tu lista de deseos"
						: "Añadir a tu lista de deseos"}
				</Button>
				<AccountPromptDialog
					open={showAccountDialog}
					onOpenChange={setShowAccountDialog}
				/>
			</>
		)
	}

	return (
		<>
			<Button
				type="button"
				variant="ghost"
				size="icon"
				onClick={handleToggle}
				className={`${className ?? ""} ${isInWishlist ? "text-red-500" : ""}`}
				aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
			>
				<Icon name={isInWishlist ? "heart-filled" : "heart"} />
			</Button>
			<AccountPromptDialog
				open={showAccountDialog}
				onOpenChange={setShowAccountDialog}
			/>
		</>
	)
}

function AccountPromptDialog({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Guarda tus favoritos</DialogTitle>
					<DialogDescription>
						Tu lista de deseos se ha guardado localmente. Crea una cuenta para
						disfrutar de estos beneficios:
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-3 py-4">
					<div className="flex items-center gap-2">
						<Icon name="heart" className="h-4 w-4 text-primary" />
						<p className="text-sm">Guarda tus productos favoritos</p>
					</div>
					<div className="flex items-center gap-2">
						<Icon name="lucide-bell" className="h-4 w-4 text-primary" />
						<p className="text-sm">Recibe alertas de cambios de precio</p>
					</div>
					<div className="flex items-center gap-2">
						<Icon name="lucide-star" className="h-4 w-4 text-primary" />
						<p className="text-sm">Notificaciones de nuevos productos</p>
					</div>
				</div>
				<DialogFooter className="flex gap-2 sm:justify-between">
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Ahora no
					</Button>
					<div className="flex gap-2">
						<Button variant="ghost" asChild>
							<a href="/login">Iniciar sesión</a>
						</Button>
						<Button asChild>
							<a href="/signup">Crear cuenta</a>
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
